import { useEffect, useState } from "react";

/**
 * Detects if a scrollable container has reached the end of horizontal scroll.
 * @param {React.RefObject} scrollRef - ref to the scrollable container (outer)
 * @param {React.RefObject} innerRef - ref to the inner element (optional, falls back to scrollRef)
 * @param {*} dep - dependency that triggers re-check (e.g. rows.length)
 */
export function useScrollEndDetection(scrollRef, innerRef, dep) {
  const [isAtScrollEnd, setIsAtScrollEnd] = useState(false);

  useEffect(() => {
    const el = innerRef?.current || scrollRef?.current;
    if (!el) return;
    const handle = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth + 2;
      if (!hasOverflow) {
        setIsAtScrollEnd(true);
        return;
      }
      const tolerance = 8;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - tolerance;
      setIsAtScrollEnd(atEnd);
    };
    requestAnimationFrame(handle);
    el.addEventListener("scroll", handle, { passive: true });
    window.addEventListener("resize", handle);
    return () => {
      el.removeEventListener("scroll", handle);
      window.removeEventListener("resize", handle);
    };
  }, [scrollRef, innerRef]);

  useEffect(() => {
    const el = innerRef?.current || scrollRef?.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const hasOverflow = el.scrollWidth > el.clientWidth + 2;
      if (!hasOverflow) {
        setIsAtScrollEnd(true);
        return;
      }
      const tolerance = 8;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - tolerance;
      setIsAtScrollEnd(atEnd);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);

  return isAtScrollEnd;
}
